import type { Uuid } from "spacetimedb";
import { useState } from "react";

import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { toaster } from "@/web/components/primitives/toast";
import { ErrorHandler } from "@/web/lib/errors";
import { formatCanonicalAddressShort } from "@/web/lib/addresses";
import { formatUSDShort } from "@/web/lib/formatting";
import { useReducerMutation } from "@/web/lib/tanstack";
import { reducers } from "@tycho/web-bindings";
import type { Property } from "@tycho/web-bindings/types";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { BudgetSlider } from "@/web/routes/_protected/smb/-dashboard/-BudgetSlider";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

/**
 * One row in the budget drawer. Each targeted property is in exactly one state:
 *   - `set`: already has a budget → show the value for reference, no slider.
 *   - `ready`: comps reviewed, no budget yet → interactive slider.
 *   - `locked`: comps not reviewed yet → disabled slider ("review comps first").
 */
type BudgetTarget = {
  property: Property;
  min: number;
  max: number;
  defaultValue: number;
  state: "set" | "ready" | "locked";
  /** The already-set budget, present only when `state === "set"`. */
  setValue?: number;
};

type MaxBudgetProps = {
  underwritableEntityId: Uuid;
  /** One entry per targeted property whose max budget is still unset. */
  targets: BudgetTarget[];
};

function MaxBudgetAction({
  underwritableEntityId,
  targets,
  onSkip,
  onComplete,
}: MaxBudgetProps & SmbActionCoreProps) {
  const [open, setOpen] = useState(false);
  const multiple = targets.length > 1;

  return (
    <>
      <ActionCard category="negotiate">
        <ActionCard.Header>
          <ActionCard.Badge>Price</ActionCard.Badge>
          <ActionCard.DetailsTrigger />
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>
            {multiple
              ? "What's the most you'd be willing to pay for each property?"
              : "What's the most you'd be willing to pay for the property?"}
          </ActionCard.Title>
          <ActionCard.Description>
            {multiple
              ? `Set a ceiling for each of your ${targets.length} properties.`
              : "Set the ceiling we'll use when evaluating offers."}
          </ActionCard.Description>
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <ActionCard.PrimaryButton onClick={() => setOpen(true)}>
            Set budget{multiple ? "s" : ""}
          </ActionCard.PrimaryButton>
        </ActionCard.Footer>
        <ActionCard.Back title="How we use this number">
          <p>
            Your answer anchors negotiations — we use it as the ceiling when evaluating
            counter-offers and structuring deal terms.
          </p>
        </ActionCard.Back>
      </ActionCard>

      <MaxBudgetDrawer
        open={open}
        underwritableEntityId={underwritableEntityId}
        targets={targets}
        onOpenChange={setOpen}
        onComplete={onComplete}
      />
    </>
  );
}

function MaxBudgetDrawer({
  open,
  underwritableEntityId,
  targets,
  onOpenChange,
  onComplete,
}: {
  open: boolean;
  underwritableEntityId: Uuid;
  targets: BudgetTarget[];
  onOpenChange: (open: boolean) => void;
  onComplete: (result?: unknown) => void;
}) {
  // One slider value per target, keyed by targetable-property id string.
  const [values, setValues] = useState<Record<string, number>>(() =>
    Object.fromEntries(targets.map((t) => [t.property.id.toString(), t.defaultValue])),
  );
  const { mutateAsync: setUserMaxPrice, isPending } = useReducerMutation(
    reducers.setUserMaxPriceForTargetableProperty,
  );

  const setValue = (id: string, next: number) => setValues((prev) => ({ ...prev, [id]: next }));

  // Only `ready` (comps-reviewed, unset) properties can have a budget set here.
  const readyTargets = targets.filter((t) => t.state === "ready");

  const handleDone = async () => {
    // Persist a max budget for each ready property. Already-set and locked
    // (un-reviewed) properties are skipped — locked ones re-surface this card
    // once their comps are reviewed. Each write is independent and idempotent,
    // so one failure must not strand the others: we set them all, then only
    // close if every one landed. A failed property keeps its slider open for a
    // retry rather than silently being left unset.
    let anyFailed = false;
    for (const target of readyTargets) {
      const id = target.property.id;
      try {
        await setUserMaxPrice({
          underwritableEntityId,
          targetablePropertyId: id,
          userMaxPrice: { value: values[id.toString()] ?? target.defaultValue, note: undefined },
        });
      } catch (error) {
        ErrorHandler.default(error);
        anyFailed = true;
      }
    }
    if (anyFailed) {
      toaster({
        message: "Some budgets couldn't be saved. Please try again.",
        variant: "error",
      });
      return;
    }
    onOpenChange(false);
    onComplete({ maxBudgets: values });
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Set your max budget{targets.length > 1 ? "s" : ""}</DrawerTitle>
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody className="flex flex-col gap-4 py-6">
          {targets.map((target) => {
            const id = target.property.id.toString();
            return (
              <section key={id} className="flex flex-col gap-1 border border-border px-4 pt-3 pb-1">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-heading text-base uppercase">
                    {formatCanonicalAddressShort(target.property.address)}
                  </h3>
                  {target.state === "set" ? (
                    <span className="withus-strong text-base text-withus-red">
                      {target.setValue !== undefined ? formatUSDShort(target.setValue) : "Set"}
                    </span>
                  ) : target.state === "locked" ? (
                    <span className="text-xs text-muted-foreground">Review comps first</span>
                  ) : null}
                </div>
                {target.state === "set" ? (
                  <p className="pb-2 text-xs text-muted-foreground">
                    You're willing to pay up to this amount.
                  </p>
                ) : (
                  <BudgetSlider
                    value={values[id] ?? target.defaultValue}
                    onChange={(next) => setValue(id, next)}
                    min={target.min}
                    max={target.max}
                    category="negotiate"
                    tone="on-surface"
                    disabled={target.state === "locked"}
                  />
                )}
              </section>
            );
          })}
        </DrawerBody>
        <DrawerFooter>
          <Button
            color="red"
            size="lg"
            isLoading={isPending}
            disabled={isPending || readyTargets.length === 0}
            onClick={handleDone}
          >
            Done
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export function createMaxBudgetAction(
  props: MaxBudgetProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "max-budget", canSkip = true, ...rest } = props;
  return {
    id,
    category: "negotiate",
    canSkip,
    render: (core) => <MaxBudgetAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createMaxBudgetCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/MaxBudgetCompleted";
import { resolvePriceEstimateNumber } from "@/web/routes/_protected/smb/-dashboard/comps";

// Slider ceiling when we have no price estimate to anchor against.
const MAX_BUDGET_FALLBACK = 5_000_000;

/** A property's comps are reviewed once it has a selected top-three. */
const compsReviewed = (p: Property) => p.selectedCompIds.length > 0;

/** A property is awaiting a budget once its comps are reviewed and none is set. */
const propertyAwaitingBudget = (p: Property) => compsReviewed(p) && p.userMaxPrice === undefined;

const budgetTargetFor = (p: Property): BudgetTarget => {
  const selectedCompIdSet = new Set(p.selectedCompIds);
  const selectedComps = p.comps.filter((c) => selectedCompIdSet.has(c.id.toString()));
  const estimate = resolvePriceEstimateNumber(p, selectedComps);
  const state: BudgetTarget["state"] =
    p.userMaxPrice !== undefined ? "set" : compsReviewed(p) ? "ready" : "locked";
  return {
    property: p,
    min: 0,
    max: estimate !== undefined ? estimate * 2 : MAX_BUDGET_FALLBACK,
    defaultValue: 0,
    state,
    setValue: p.userMaxPrice?.value,
  };
};

// Single card across every targeted property: shows when ANY reviewed property
// still has no max budget. The drawer lists *every* targeted property — already
// set ones show their value for reference, reviewed-unset ones get an
// interactive slider, and un-reviewed ones a disabled one ("review comps
// first"). Leaves the deck once every reviewed property has a budget.
//
// Hidden for invited brands.
export const maxBudgetRule = CardRulesEngine.singletonRule({
  id: "max-budget",
  priority: 60,
  when: CardRulesEngine.and(
    (d) => d.properties.some(propertyAwaitingBudget),
    CardRulesEngine.not(CardRulesEngine.businessStoryIs("Invited")),
  ),
  build: (d) =>
    createMaxBudgetAction({
      underwritableEntityId: d.underwritableEntityId,
      targets: d.properties.map(budgetTargetFor),
    }),
});

// One completed card per property that has a max budget (unchanged axis).
// Hidden for invited brands.
export const maxBudgetCompletedRule = CardRulesEngine.forEachCompletedRule({
  id: "max-budget:done",
  forEach: CardRulesEngine.property,
  when: CardRulesEngine.and(
    CardRulesEngine.propertyHasMaxPrice,
    CardRulesEngine.not(CardRulesEngine.businessStoryIs("Invited")),
  ),
  build: (_, p) => createMaxBudgetCompleted({ property: p }),
});
